import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

export interface GroupData {
  id: string;
  name: string;
  description: string;
  color?: string;
  parent_id?: string;
  created_at: string;
  updated_at: string;
  member_count: number;
  subgroups?: GroupData[];
  members?: GroupMember[];
}

export interface GroupMember {
  id: string;
  first_name: string;
  last_name: string;
  phone?: string;
  joined_at: string;
  role?: string | null;
  permissions?: any;
  isAdmin?: boolean;
}

export class SupabaseGroupsService {
  constructor(private readonly client: SupabaseClient<Database>) {}

  async getGroups(): Promise<GroupData[]> {
    // Fetch groups, then load members and profiles separately (avoid embedded errors)
    console.log('[SupabaseGroupsService] Starting getGroups');
    const base = await this.client
      .from('groups')
      .select('*')
      .order('created_at', { ascending: true });
    if (base.error || !base.data) {
      console.error('[SupabaseGroupsService] Error fetching groups:', base.error);
      return [];
    }
    const groups = base.data as any[];
    console.log('[SupabaseGroupsService] Fetched groups:', {
      count: groups.length,
      groups: groups.map(g => ({ id: g.id, name: g.name, parent_id: g.parent_id }))
    });

    const groupIds = groups.map(g => g.id);
    const gmRows: any[] = [];
    if (groupIds.length) {
      const PAGE_SIZE = 1000;
      let from = 0;
      // Range end is inclusive in Supabase; keep it in sync with `from`
      while (true) {
        try {
          const to = from + PAGE_SIZE - 1;
          const gmRes = await this.client
            .from('group_members')
            .select('group_id,user_id,joined_at')
            .in('group_id', groupIds)
            .range(from, to);

          if (gmRes.error) {
            console.error('[SupabaseGroupsService] Failed to load group membership batch', gmRes.error);
            break;
          }

          const chunk = gmRes.data || [];
          gmRows.push(...chunk);

          if (!chunk.length || chunk.length < PAGE_SIZE) {
            break;
          }

          from += PAGE_SIZE;
        } catch (e) {
          console.error('Failed to load group membership batch', e);
          break;
        }
      }
    }
    const userIds = Array.from(new Set(gmRows.map((r: any) => r.user_id)));
    const profileMap = new Map<string, any>();
    if (userIds.length) {
      const pRes = await this.client
        .from('profiles_public_v')
        .select('user_id,first_name,last_name,phone')
        .in('user_id', userIds);
      (pRes.data || []).forEach((p: any) => profileMap.set(p.user_id, p));
    }
    // Optional admin map
    const adminMap = new Map<string, boolean>();
    try {
      if (userIds.length) {
        const aRes = await this.client
          .from('user_admin_map_v')
          .select('user_id')
          .in('user_id', userIds);
        (aRes.data || []).forEach((r: any) => adminMap.set(r.user_id, true));
      }
    } catch (error) {
      console.debug('Failed to load admin map', error);
    }

    // Transform the data to match our interface
    const transformedGroups =
      groups?.map(group => {
        const gmForGroup = gmRows.filter((r: any) => r.group_id === group.id);
        // Map and de-duplicate members by user_id
        const rawMembers: GroupMember[] = gmForGroup.map((gm: any) => {
          const prof = profileMap.get(gm.user_id) || {};
          return {
            id: gm.user_id,
            first_name: prof.first_name || '',
            last_name: prof.last_name || '',
            phone: prof.phone,
            joined_at: gm.joined_at,
            isAdmin: adminMap.get(gm.user_id) || false,
          } as GroupMember;
        });
        const seen = new Set<string>();
        const members = rawMembers
          .filter(m => {
            if (seen.has(m.id)) return false;
            seen.add(m.id);
            return true;
          })
          .sort((a, b) => {
            const an = `${a.last_name ?? ''} ${a.first_name ?? ''}`.trim().toLowerCase();
            const bn = `${b.last_name ?? ''} ${b.first_name ?? ''}`.trim().toLowerCase();
            return an.localeCompare(bn);
          });

        return {
          id: group.id,
          name: group.name,
          description: group.description || '',
          color: group.color || '#3b82f6',
          parent_id: group.parent_id,
          created_at: group.created_at,
          updated_at: group.updated_at,
          member_count: members.length,
          members,
          subgroups: [],
        };
      }) || [];

    // Organize into parent-child relationships
    const groupMap = new Map<string, GroupData>();
    const rootGroups: GroupData[] = [];

    // First pass: create map and identify root groups
    transformedGroups.forEach(group => {
      groupMap.set(group.id, group);
      if (!group.parent_id) {
        rootGroups.push(group);
      }
    });

    // Second pass: organize subgroups
    transformedGroups.forEach(group => {
      if (group.parent_id && groupMap.has(group.parent_id)) {
        const parent = groupMap.get(group.parent_id)!;
        if (!parent.subgroups) {
          parent.subgroups = [];
        }
        parent.subgroups.push(group);
      }
    });

    return rootGroups;
  }

  async createGroup(name: string, description: string, parentId?: string): Promise<boolean> {
    const { error } = await this.client
      .from('groups')
      .insert({
        name,
        description,
        parent_id: parentId,
        color: '#3b82f6',
      });

    if (error) {
      console.error('Error creating group:', error);
      return false;
    }

    return true;
  }

  async addMemberToGroup(groupId: string, userId: string): Promise<boolean> {
    const { error } = await this.client
      .from('group_members')
      .insert({
        group_id: groupId,
        user_id: userId,
      });

    if (error) {
      console.error('Error adding member to group:', error);
      return false;
    }

    return true;
  }

  async removeMemberFromGroup(groupId: string, userId: string): Promise<boolean> {
    const { error } = await this.client
      .from('group_members')
      .delete()
      .match({
        group_id: groupId,
        user_id: userId,
      });

    if (error) {
      console.error('Error removing member from group:', error);
      return false;
    }

    return true;
  }
}
